import axios from 'axios'
import { message } from 'antd'
import Agilite from 'agilite'
import { formatCurrency } from './helpers'
import dayjs from 'dayjs'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

// Helper function to get current entity and user info
const getCurrentContext = () => {
  try {
    // Try to get from window.store first
    if (window.store && window.store.getState) {
      const state = window.store.getState()
      return {
        entityId: state.core?.entity?._id,
        entityName: state.core?.entity?.name || 'Meta Health Clinic',
        entityEmail: state.core?.entity?.email || 'billing@metahealth.co.za',
        entityPhone: state.core?.entity?.phone || '+27 11 123 4567',
        userId: state.core?.user?._id
      }
    }

    // Fallback to localStorage or default values
    const entityData = JSON.parse(localStorage.getItem('currentEntity') || '{}')
    const userData = JSON.parse(localStorage.getItem('currentUser') || '{}')

    return {
      entityId: entityData._id || 'default-entity',
      entityName: entityData.name || 'Meta Health Clinic',
      entityEmail: entityData.email || 'billing@metahealth.co.za',
      entityPhone: entityData.phone || '+27 11 123 4567',
      userId: userData._id || 'system'
    }
  } catch (error) {
    console.warn('Could not get current context, using defaults:', error)
    return {
      entityId: 'default-entity',
      entityName: 'Meta Health Clinic',
      entityEmail: 'billing@metahealth.co.za',
      entityPhone: '+27 11 123 4567',
      userId: 'system'
    }
  }
}

// Communication types enum
export const CommunicationTypes = {
  WHATSAPP: 'whatsapp',
  SMS: 'sms',
  EMAIL: 'email',
  CALL: 'call'
}

// Communication templates enum
export const CommunicationTemplates = {
  PAYMENT_REMINDER: 'payment_reminder',
  PAYMENT_REQUEST: 'payment_request',
  PAYMENT_OVERDUE: 'payment_overdue',
  PAYMENT_PLAN_OFFER: 'payment_plan_offer',
  SETTLEMENT_OFFER: 'settlement_offer',
  FINAL_NOTICE: 'final_notice',
  PAYMENT_CONFIRMATION: 'payment_confirmation',
  MEDICAL_AID_QUERY: 'medical_aid_query'
}

// Template configurations
const TEMPLATE_CONFIG = {
  [CommunicationTemplates.PAYMENT_REMINDER]: {
    whatsapp: 'payment_reminder_wa',
    sms: 'payment_reminder_sms',
    email: 'payment_reminder_email',
    subject: 'Payment Reminder - Account {{billingId}}'
  },
  [CommunicationTemplates.PAYMENT_REQUEST]: {
    whatsapp: 'payment_request_wa',
    sms: 'payment_request_sms',
    email: 'payment_request_email',
    subject: 'Payment Request - Account {{billingId}}'
  },
  [CommunicationTemplates.PAYMENT_OVERDUE]: {
    whatsapp: 'payment_overdue_wa',
    sms: 'payment_overdue_sms',
    email: 'payment_overdue_email',
    subject: 'Overdue Payment Notice - Account {{billingId}}'
  },
  [CommunicationTemplates.PAYMENT_PLAN_OFFER]: {
    whatsapp: 'payment_plan_wa',
    sms: 'payment_plan_sms',
    email: 'payment_plan_email',
    subject: 'Payment Plan Option - Account {{billingId}}'
  },
  [CommunicationTemplates.SETTLEMENT_OFFER]: {
    whatsapp: 'settlement_offer_wa',
    sms: 'settlement_offer_sms',
    email: 'settlement_offer_email',
    subject: 'Settlement Offer - Account {{billingId}}'
  },
  [CommunicationTemplates.FINAL_NOTICE]: {
    whatsapp: 'final_notice_wa',
    sms: 'final_notice_sms',
    email: 'final_notice_email',
    subject: 'Final Payment Notice - Account {{billingId}}'
  },
  [CommunicationTemplates.PAYMENT_CONFIRMATION]: {
    whatsapp: 'payment_confirmation_wa',
    sms: 'payment_confirmation_sms',
    email: 'payment_confirmation_email',
    subject: 'Payment Received - Account {{billingId}}'
  },
  [CommunicationTemplates.MEDICAL_AID_QUERY]: {
    whatsapp: 'medical_aid_query_wa',
    sms: 'medical_aid_query_sms',
    email: 'medical_aid_query_email',
    subject: 'Medical Aid Claim Query - Account {{billingId}}'
  }
}

/**
 * Replace template variables with actual values
 * @param {string} template - Template string
 * @param {Object} variables - Variables to replace
 * @returns {string} - Processed template
 */
const processTemplate = (template, variables) => {
  let processed = template
  Object.keys(variables).forEach((key) => {
    const regex = new RegExp(`{{${key}}}`, 'g')
    processed = processed.replace(regex, variables[key] || '')
  })
  return processed
}

/**
 * Log communication activity to database
 * @param {Object} communicationData - Communication details
 */
const logCommunication = async (communicationData) => {
  try {
    const context = getCurrentContext()
    const logData = {
      entityRef: context.entityId,
      ...communicationData,
      timestamp: new Date(),
      createdBy: context.userId,
      createdAt: new Date()
    }

    await agilite.Connectors.execute('billing_communications', 'CREATE', logData)
  } catch (error) {
    console.error('Failed to log communication:', error)
  }
}

/**
 * Send WhatsApp message
 * @param {Object} params - WhatsApp message parameters
 * @returns {Promise} - WhatsApp API response
 */
export const sendWhatsAppMessage = async (params) => {
  const { to, templateType, accountData, customMessage, variables = {} } = params

  try {
    // Clean phone number (remove spaces, dashes, etc.)
    const cleanPhone = to.replace(/[^\d+]/g, '')

    // Prepare template variables
    const context = getCurrentContext()
    const templateVars = {
      patientName: accountData?.patientName || 'Valued Patient',
      billingId: accountData?.billingId || accountData?.accountNo || '',
      outstandingAmount: formatCurrency(accountData?.outstandingAmount || 0),
      daysOutstanding: accountData?.daysOutstanding || 0,
      clinicName: context.entityName,
      ...variables
    }

    let requestBody

    if (customMessage) {
      // Send custom text message
      requestBody = {
        messaging_product: 'whatsapp',
        to: cleanPhone,
        type: 'text',
        text: {
          body: processTemplate(customMessage, templateVars)
        }
      }
    } else {
      // Send template message
      const template = TEMPLATE_CONFIG[templateType]
      if (!template) {
        throw new Error(`Template not found: ${templateType}`)
      }

      requestBody = {
        messaging_product: 'whatsapp',
        to: cleanPhone,
        type: 'template',
        template: {
          name: template.whatsapp,
          language: {
            code: 'en'
          },
          components: [
            {
              type: 'body',
              parameters: Object.values(templateVars).map((value) => ({
                type: 'text',
                text: String(value)
              }))
            }
          ]
        }
      }
    }

    const response = await axios.post(`${process.env.REACT_APP_NODE_RED_URL}/api/whatsapp/send`, requestBody, {
      headers: {
        'Content-Type': 'application/json',
        'api-key': process.env.REACT_APP_AGILITE_API_KEY
      }
    })

    // Log successful communication
    await logCommunication({
      type: CommunicationTypes.WHATSAPP,
      templateType: templateType || 'custom',
      recipient: cleanPhone,
      billingId: accountData?.billingId,
      patientId: accountData?.patientId,
      status: 'sent',
      message: customMessage || `Template: ${templateType}`,
      response: response.data
    })

    return {
      success: true,
      data: response.data,
      messageId: response.data?.messages?.[0]?.id
    }
  } catch (error) {
    console.error('WhatsApp send error:', error)

    // Log failed communication
    await logCommunication({
      type: CommunicationTypes.WHATSAPP,
      templateType: templateType || 'custom',
      recipient: to,
      billingId: accountData?.billingId,
      patientId: accountData?.patientId,
      status: 'failed',
      message: customMessage || `Template: ${templateType}`,
      error: error.message
    })

    throw error
  }
}

/**
 * Send SMS message
 * @param {Object} params - SMS message parameters
 * @returns {Promise} - SMS API response
 */
export const sendSMSMessage = async (params) => {
  const { to, templateType, accountData, customMessage, variables = {} } = params

  try {
    // Clean phone number
    const cleanPhone = to.replace(/[^\d+]/g, '')

    // Prepare template variables
    const context = getCurrentContext()
    const templateVars = {
      patientName: accountData?.patientName || 'Valued Patient',
      billingId: accountData?.billingId || accountData?.accountNo || '',
      outstandingAmount: formatCurrency(accountData?.outstandingAmount || 0),
      daysOutstanding: accountData?.daysOutstanding || 0,
      clinicName: context.entityName,
      ...variables
    }

    let messageText

    if (customMessage) {
      messageText = processTemplate(customMessage, templateVars)
    } else {
      // Get template configuration
      const template = TEMPLATE_CONFIG[templateType]
      if (!template) {
        throw new Error(`Template not found: ${templateType}`)
      }

      // For SMS, we'll use predefined message templates
      const smsTemplates = {
        [CommunicationTemplates.PAYMENT_REMINDER]:
          'Hi {{patientName}}, this is a friendly reminder that you have an outstanding balance of {{outstandingAmount}} on account {{billingId}}. Please settle at your earliest convenience. - {{clinicName}}',
        [CommunicationTemplates.PAYMENT_REQUEST]:
          'Dear {{patientName}}, we kindly request payment of {{outstandingAmount}} for account {{billingId}}. For payment options, please contact us. - {{clinicName}}',
        [CommunicationTemplates.PAYMENT_OVERDUE]:
          'URGENT: {{patientName}}, your account {{billingId}} is {{daysOutstanding}} days overdue with balance {{outstandingAmount}}. Please settle immediately to avoid further action. - {{clinicName}}',
        [CommunicationTemplates.FINAL_NOTICE]:
          'FINAL NOTICE: {{patientName}}, account {{billingId}} balance {{outstandingAmount}} requires immediate payment. Contact us today to avoid collection action. - {{clinicName}}'
      }

      messageText = processTemplate(
        smsTemplates[templateType] || smsTemplates[CommunicationTemplates.PAYMENT_REMINDER],
        templateVars
      )
    }

    const response = await axios.post(
      `${process.env.REACT_APP_NODE_RED_URL}/api/sms/send`,
      {
        to: cleanPhone,
        message: messageText
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'api-key': process.env.REACT_APP_AGILITE_API_KEY
        }
      }
    )

    // Log successful communication
    await logCommunication({
      type: CommunicationTypes.SMS,
      templateType: templateType || 'custom',
      recipient: cleanPhone,
      billingId: accountData?.billingId,
      patientId: accountData?.patientId,
      status: 'sent',
      message: messageText,
      response: response.data
    })

    return {
      success: true,
      data: response.data,
      message: messageText
    }
  } catch (error) {
    console.error('SMS send error:', error)

    // Log failed communication
    await logCommunication({
      type: CommunicationTypes.SMS,
      templateType: templateType || 'custom',
      recipient: to,
      billingId: accountData?.billingId,
      patientId: accountData?.patientId,
      status: 'failed',
      message: customMessage || `Template: ${templateType}`,
      error: error.message
    })

    throw error
  }
}

/**
 * Send Email message
 * @param {Object} params - Email message parameters
 * @returns {Promise} - Email API response
 */
export const sendEmailMessage = async (params) => {
  const { to, templateType, accountData, customMessage, customSubject, variables = {}, attachments = [] } = params

  try {
    // Prepare template variables
    const context = getCurrentContext()
    const templateVars = {
      patientName: accountData?.patientName || 'Valued Patient',
      billingId: accountData?.billingId || accountData?.accountNo || '',
      outstandingAmount: formatCurrency(accountData?.outstandingAmount || 0),
      daysOutstanding: accountData?.daysOutstanding || 0,
      clinicName: context.entityName,
      clinicEmail: context.entityEmail,
      clinicPhone: context.entityPhone,
      ...variables
    }

    let subject, htmlContent

    if (customMessage && customSubject) {
      subject = processTemplate(customSubject, templateVars)
      htmlContent = processTemplate(customMessage, templateVars)
    } else {
      // Get template configuration
      const template = TEMPLATE_CONFIG[templateType]
      if (!template) {
        throw new Error(`Template not found: ${templateType}`)
      }

      subject = processTemplate(template.subject, templateVars)

      // HTML email templates
      const emailTemplates = {
        [CommunicationTemplates.PAYMENT_REMINDER]: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <div style="background: #1890ff; color: white; padding: 20px; text-align: center;">
              <h2>{{clinicName}}</h2>
              <p>Payment Reminder</p>
            </div>
            <div style="padding: 30px; background: #f9f9f9;">
              <p>Dear {{patientName}},</p>
              <p>This is a friendly reminder that you have an outstanding balance on your account.</p>
              <div style="background: white; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <table style="width: 100%;">
                  <tr><td><strong>Account Number:</strong></td><td>{{billingId}}</td></tr>
                  <tr><td><strong>Outstanding Amount:</strong></td><td style="color: #cf1322; font-weight: bold;">{{outstandingAmount}}</td></tr>
                  <tr><td><strong>Days Outstanding:</strong></td><td>{{daysOutstanding}} days</td></tr>
                </table>
              </div>
              <p>Please settle this amount at your earliest convenience to avoid any inconvenience.</p>
              <p>If you have any questions or need to arrange a payment plan, please don't hesitate to contact us.</p>
              <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd;">
                <p><strong>Contact Information:</strong></p>
                <p>Email: {{clinicEmail}}<br>Phone: {{clinicPhone}}</p>
              </div>
            </div>
          </div>
        `,
        [CommunicationTemplates.PAYMENT_OVERDUE]: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <div style="background: #ff4d4f; color: white; padding: 20px; text-align: center;">
              <h2>{{clinicName}}</h2>
              <p>⚠️ OVERDUE PAYMENT NOTICE</p>
            </div>
            <div style="padding: 30px; background: #f9f9f9;">
              <p>Dear {{patientName}},</p>
              <p><strong>URGENT:</strong> Your account is significantly overdue and requires immediate attention.</p>
              <div style="background: #fff2f0; border-left: 4px solid #ff4d4f; padding: 20px; margin: 20px 0;">
                <table style="width: 100%;">
                  <tr><td><strong>Account Number:</strong></td><td>{{billingId}}</td></tr>
                  <tr><td><strong>Outstanding Amount:</strong></td><td style="color: #cf1322; font-weight: bold; font-size: 18px;">{{outstandingAmount}}</td></tr>
                  <tr><td><strong>Days Overdue:</strong></td><td style="color: #cf1322; font-weight: bold;">{{daysOutstanding}} days</td></tr>
                </table>
              </div>
              <p><strong>This account requires immediate payment to avoid further collection action.</strong></p>
              <p>Please contact us immediately to arrange payment or discuss payment options.</p>
              <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd;">
                <p><strong>Contact Information:</strong></p>
                <p>Email: {{clinicEmail}}<br>Phone: {{clinicPhone}}</p>
              </div>
            </div>
          </div>
        `
      }

      htmlContent = processTemplate(
        emailTemplates[templateType] || emailTemplates[CommunicationTemplates.PAYMENT_REMINDER],
        templateVars
      )
    }

    const response = await axios.post(
      `${process.env.REACT_APP_NODE_RED_URL}/api/email/send`,
      {
        to: to,
        subject: subject,
        html: htmlContent,
        attachments: attachments
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'api-key': process.env.REACT_APP_AGILITE_API_KEY
        }
      }
    )

    // Log successful communication
    await logCommunication({
      type: CommunicationTypes.EMAIL,
      templateType: templateType || 'custom',
      recipient: to,
      billingId: accountData?.billingId,
      patientId: accountData?.patientId,
      status: 'sent',
      subject: subject,
      message: htmlContent,
      response: response.data
    })

    return {
      success: true,
      data: response.data,
      subject: subject
    }
  } catch (error) {
    console.error('Email send error:', error)

    // Log failed communication
    await logCommunication({
      type: CommunicationTypes.EMAIL,
      templateType: templateType || 'custom',
      recipient: to,
      billingId: accountData?.billingId,
      patientId: accountData?.patientId,
      status: 'failed',
      subject: customSubject || `Template: ${templateType}`,
      message: customMessage || `Template: ${templateType}`,
      error: error.message
    })

    throw error
  }
}

/**
 * Record a phone call communication
 * @param {Object} params - Call record parameters
 */
export const recordPhoneCall = async (params) => {
  const { patientPhone, accountData, callOutcome, notes, duration, followUpDate } = params

  try {
    await logCommunication({
      type: CommunicationTypes.CALL,
      templateType: 'phone_call',
      recipient: patientPhone,
      billingId: accountData?.billingId,
      patientId: accountData?.patientId,
      status: callOutcome, // 'completed', 'no_answer', 'busy', 'voicemail'
      message: notes,
      duration: duration,
      followUpDate: followUpDate
    })

    return { success: true }
  } catch (error) {
    console.error('Failed to record phone call:', error)
    throw error
  }
}

/**
 * Get communication history for an account
 * @param {string} billingId - Account billing ID
 * @returns {Promise} - Communication history
 */
export const getCommunicationHistory = async (billingId) => {
  try {
    const context = getCurrentContext()
    const response = await agilite.Connectors.execute('billing_communications', 'FIND', {
      filter: {
        billingId: billingId,
        entityRef: context.entityId
      },
      sort: { timestamp: -1 },
      pageLimit: 100
    })

    return response.data || []
  } catch (error) {
    console.error('Failed to get communication history:', error)
    return []
  }
}

/**
 * Bulk send communications to multiple accounts
 * @param {Array} accounts - List of account data
 * @param {Object} communicationParams - Communication parameters
 * @returns {Promise} - Bulk send results
 */
export const bulkSendCommunications = async (accounts, communicationParams) => {
  const { type, templateType, customMessage, customSubject } = communicationParams
  const results = {
    successful: [],
    failed: []
  }

  for (const account of accounts) {
    try {
      let contactInfo

      // Get patient contact information
      try {
        const patientData = await agilite.Connectors.execute('system_users', 'FIND_BY_ID', {
          id: account.patientId
        })
        contactInfo = patientData.data
      } catch (err) {
        throw new Error('Failed to get patient contact information')
      }

      const params = {
        templateType,
        accountData: account,
        customMessage,
        customSubject
      }

      let result
      switch (type) {
        case CommunicationTypes.WHATSAPP:
          if (!contactInfo.cellNo) throw new Error('No WhatsApp number available')
          result = await sendWhatsAppMessage({ ...params, to: contactInfo.cellNo })
          break

        case CommunicationTypes.SMS:
          if (!contactInfo.cellNo) throw new Error('No SMS number available')
          result = await sendSMSMessage({ ...params, to: contactInfo.cellNo })
          break

        case CommunicationTypes.EMAIL:
          if (!contactInfo.email) throw new Error('No email address available')
          result = await sendEmailMessage({ ...params, to: contactInfo.email })
          break

        default:
          throw new Error(`Unsupported communication type: ${type}`)
      }

      results.successful.push({
        accountId: account._id,
        billingId: account.billingId,
        patientName: account.patientName,
        result
      })
    } catch (error) {
      results.failed.push({
        accountId: account._id,
        billingId: account.billingId,
        patientName: account.patientName,
        error: error.message
      })
    }

    // Add delay between messages to avoid rate limiting
    await new Promise((resolve) => setTimeout(resolve, 1000))
  }

  return results
}

export default {
  CommunicationTypes,
  CommunicationTemplates,
  sendWhatsAppMessage,
  sendSMSMessage,
  sendEmailMessage,
  recordPhoneCall,
  getCommunicationHistory,
  bulkSendCommunications
}
